import React, {useLayoutEffect, useReducer, useRef} from "react";
import persianNumberFormat from "../../helper/PersianNumberFormat";

function normalizeAmount(value) {
  return String(value ?? "").trim()
    .replace(/[۰-۹]/g, digit => "۰۱۲۳۴۵۶۷۸۹".indexOf(digit))
    .replace(/[٠-٩]/g, digit => "٠١٢٣٤٥٦٧٨٩".indexOf(digit))
    .replace(/[,٬]/g, "");
}

function formatAmount(value) {
  const amount = normalizeAmount(value);
  return /^\d*$/.test(amount) ? persianNumberFormat(amount, true) : String(value ?? "");
}

function digitCount(value) {
  return (value.match(/[0-9۰-۹٠-٩]/g) || []).length;
}

function positionAfterDigits(value, count) {
  if (count === 0) return 0;
  let seen = 0;
  for (let index = 0; index < value.length; index += 1) {
    if (/[0-9۰-۹٠-٩]/.test(value[index]) && ++seen === count) return index + 1;
  }
  return value.length;
}

// Whole-rial amounts stay as strings until the caller validates and submits.
// Invalid text remains visible instead of being stripped into a different amount.
export function MoneyInput({value, onValueChange, onKeyDown, ...props}) {
  const inputRef = useRef(null);
  const pendingSelection = useRef(null);
  const [editRevision, nextEdit] = useReducer(revision => revision + 1, 0);
  const displayValue = formatAmount(value);

  useLayoutEffect(() => {
    const selection = pendingSelection.current;
    pendingSelection.current = null;
    if (!selection || selection.value !== String(value ?? "") || document.activeElement !== inputRef.current) return;
    inputRef.current.setSelectionRange(selection.start, selection.end, selection.direction);
  }, [value, editRevision]);

  const change = (text, start, end, direction = "none") => {
    const normalized = normalizeAmount(text);
    const valid = /^\d*$/.test(normalized);
    const nextValue = valid ? normalized : text;
    const formatted = formatAmount(nextValue);
    pendingSelection.current = {
      value: nextValue,
      start: valid ? positionAfterDigits(formatted, digitCount(text.slice(0, start))) : start,
      end: valid ? positionAfterDigits(formatted, digitCount(text.slice(0, end))) : end,
      direction,
    };
    // Separator-only edits also need a render to restore the caret.
    nextEdit();
    onValueChange(nextValue);
  };

  const handleKeyDown = event => {
    onKeyDown?.(event);
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
    const {selectionStart: start, selectionEnd: end} = event.currentTarget;
    if (start !== end || !/^\d*$/.test(normalizeAmount(value))) return;
    // Deleting beside a generated separator should remove the adjacent digit.
    if (event.key === "Backspace" && start > 1 && displayValue[start - 1] === ",") {
      event.preventDefault();
      change(displayValue.slice(0, start - 2) + displayValue.slice(start), start - 2, start - 2);
    } else if (event.key === "Delete" && displayValue[start] === ",") {
      event.preventDefault();
      change(displayValue.slice(0, start) + displayValue.slice(start + 2), start, start);
    }
  };

  return <input
    {...props} ref={inputRef} type="text" inputMode="numeric" dir="ltr" value={displayValue}
    onChange={event => change(event.target.value, event.target.selectionStart, event.target.selectionEnd, event.target.selectionDirection)}
    onKeyDown={handleKeyDown}
  />;
}
